import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PosProvider, usePos, POS_HOME } from './contexts/PosContext';
import Layout from './components/layout/Layout';
import Login from './pages/auth/Login';
import ChangePassword from './pages/auth/ChangePassword';
import AcceptTerms from './pages/auth/AcceptTerms';
import Activate from './pages/pos/Activate';
import { FISCALIZATION_ENABLED } from './config/features';

// Split per page: the installed till only ever routes to fiscalization, products and settings, so
// the back-office pages are never fetched or parsed on a machine at the counter.
const Register = lazy(() => import('./pages/auth/Register'));
const ConfirmEmail = lazy(() => import('./pages/auth/ConfirmEmail'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Products = lazy(() => import('./pages/products/Products'));
const Branches = lazy(() => import('./pages/branches/Branches'));
const Workers = lazy(() => import('./pages/workers/Workers'));
const Billing = lazy(() => import('./pages/billing/Billing'));
const ECDeclarations = lazy(() => import('./pages/ECDeclarations'));
const Clients = lazy(() => import('./pages/clients/Clients'));
const Invoices = lazy(() => import('./pages/invoices/Invoices'));
const Orders = lazy(() => import('./pages/orders/Orders'));
const Fiscalization = lazy(() => import('./pages/fiscalization/Fiscalization'));
const Notifications = lazy(() => import('./pages/notifications/Notifications'));
const Admin = lazy(() => import('./pages/admin/Admin'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const Catalog = lazy(() => import('./pages/public/Catalog'));
// The public front page. Lazy like the rest: an installed till never routes to it, so it is never
// fetched on the machine at the counter.
const Landing = lazy(() => import('./pages/public/Landing'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000 } }
});

function PrivateRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

function OwnerRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'Owner') return <Navigate to="/" replace />;
  return children;
}

function ManagerRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'Owner' && user.role !== 'Manager') return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isPlatformAdmin) return <Navigate to="/" replace />;
  return children;
}

/**
 * The routes of an installed till: the branch's catalogue, fiscalization, and the till's own
 * settings. A till has no dashboard, no back office and no billing — those are decisions taken in
 * the office, and loading them here only slows the counter down.
 */
function PosRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/fiscalization" element={<PrivateRoute><Layout><Fiscalization /></Layout></PrivateRoute>} />
      <Route path="/products" element={<PrivateRoute><Layout><Products /></Layout></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Layout><Settings /></Layout></PrivateRoute>} />
      <Route path="*" element={<Navigate to={POS_HOME} replace />} />
    </Routes>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const { isPos } = usePos();

  // The platform admin is an operator, not a tenant — it only ever sees the Admin dashboard and its
  // own account settings. It bypasses the company-oriented gates (password change / terms) entirely.
  if (user?.isPlatformAdmin) {
    return (
      <Routes>
        <Route path="/admin" element={<Layout><Admin /></Layout>} />
        <Route path="/settings" element={<Layout><Settings /></Layout>} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    );
  }

  // A worker created with an auto-generated password must change it before doing anything else.
  if (user?.mustChangePassword) {
    return (
      <Routes>
        {!isPos && <Route path="/catalog/:token" element={<Catalog />} />}
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="*" element={<Navigate to="/change-password" replace />} />
      </Routes>
    );
  }

  // Once logged in, the user must accept the current Terms & Conditions before using the app.
  if (user?.requiresTermsAcceptance) {
    return (
      <Routes>
        {!isPos && <Route path="/catalog/:token" element={<Catalog />} />}
        <Route path="/accept-terms" element={<AcceptTerms />} />
        <Route path="*" element={<Navigate to="/accept-terms" replace />} />
      </Routes>
    );
  }

  if (isPos) return <PosRoutes />;

  return (
    <Routes>
      {/* Public, unauthenticated catalog — no Layout, no auth */}
      <Route path="/catalog/:token" element={<Catalog />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* The root is the public landing page until someone is logged in — a visitor should meet
          what the product does, not a login form for an account they may not have yet. */}
      <Route path="/" element={user ? <Layout><Dashboard /></Layout> : <Landing />} />
      <Route path="/products" element={<PrivateRoute><Layout><Products /></Layout></PrivateRoute>} />
      <Route path="/branches" element={<ManagerRoute><Layout><Branches /></Layout></ManagerRoute>} />
      <Route path="/workers" element={<ManagerRoute><Layout><Workers /></Layout></ManagerRoute>} />
      <Route path="/ec-declarations" element={<PrivateRoute><Layout><ECDeclarations /></Layout></PrivateRoute>} />
      <Route path="/clients" element={<ManagerRoute><Layout><Clients /></Layout></ManagerRoute>} />
      <Route path="/invoices" element={<ManagerRoute><Layout><Invoices /></Layout></ManagerRoute>} />
      <Route path="/orders" element={<ManagerRoute><Layout><Orders /></Layout></ManagerRoute>} />
      {/* Off while the module is not offered (config/features) — the address then falls through to
          the catch-all below, so a bookmark lands on the dashboard rather than on a dead page.
          PosRoutes above is deliberately not gated: a till is only ever this page. */}
      {FISCALIZATION_ENABLED && <Route path="/fiscalization" element={<PrivateRoute><Layout><Fiscalization /></Layout></PrivateRoute>} />}
      <Route path="/notifications" element={<PrivateRoute><Layout><Notifications /></Layout></PrivateRoute>} />
      <Route path="/billing" element={<OwnerRoute><Layout><Billing /></Layout></OwnerRoute>} />
      <Route path="/admin" element={<AdminRoute><Layout><Admin /></Layout></AdminRoute>} />
      <Route path="/settings" element={<PrivateRoute><Layout><Settings /></Layout></PrivateRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * On an installed till the app cannot show anything useful until the PC has been bound to a
 * branch — it has no company, catalogue or users before its first sync. In cloud mode the status
 * call returns isPosMode:false and this is a no-op.
 *
 * An unpaid till is *not* locked out here: the server refuses to issue coupons (see
 * RequiresFiscalizationAddon) while reading and exporting the records it already produced stays
 * open, which is the point. The banner in Layout says so.
 */
function PosGate({ children }) {
  const { isPos, isActivated, isLoading, refetch } = usePos();

  if (isLoading) return null;
  if (isPos && !isActivated) return <Activate onActivated={() => refetch()} />;
  return children;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PosProvider>
          <PosGate>
            <AuthProvider>
              <Suspense fallback={null}>
                <AppRoutes />
              </Suspense>
            </AuthProvider>
          </PosGate>
        </PosProvider>
        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
